import { Button, ButtonGroup, ConfirmDialog, Field, NumericInput, TextInput, TextOutput } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { FieldSet, ListFieldSection, SectionGroup } from 'Components/Layout';
import {
  IWithWorksheetInjectedProps,
  WorksheetBalanceFieldSet,
  WorksheetEquipmentDropdown,
} from 'Components/Worksheets/Core';
import { actions } from 'Data/ReduxViewModels/WorksheetEditor';
import { ISieveSize } from 'Data/ViewModels/Equipment/ISieveSize';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import { orderBy as deprecatedOrderBy } from 'lodash';
import { isNil } from 'ramda';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OversizeMaterialRemovedByType } from '../../../types';

/**
 * @deprecated Use ramda sortBy not lodash orderBy
 */
const sortBy = deprecatedOrderBy;

let BsEnIsoPreparationMaterialMassSection: React.FC<IWithWorksheetInjectedProps> = ({
  createWorksheetSelector,
  prefixName,
}) => {
  const dispatch = useDispatch();

  const oversizeSieveApertureSize = useSelector(createWorksheetSelector!('OversizeSieveApertureSize'), deepEquals);
  const availableSieveSizes: ISieveSize[] = useSelector(createWorksheetSelector!('AvailableSieveSizes'), deepEquals);
  const oversizeMaterialRemovedBy = useSelector(
    createWorksheetSelector!('OversizeMaterialRemovedBy'),
    deepEquals
  ) as OversizeMaterialRemovedByType;

  const guardSieves = useSelector(createWorksheetSelector!('GuardSieves'), deepEquals) as any[];

  const updateMoulds = (value: any[]) => {
    dispatch(
      actions.setValue({
        name: prefixName!('GuardSieves'),
        value: value,
        sync: true,
      })
    );
  };

  var selectedOversizeSieveApertureSize: any = availableSieveSizes?.find((o) => o.Id === oversizeSieveApertureSize);

  // TODO: Only need to obtain this because our API does not allow for strict inequalities. :(
  const guardSieveMinimumSieveSize = useSelector(
    pipe(createWorksheetSelector!('AllSieveSizes'), (sieveSizes: ISieveSize[]) => {
      if (!sieveSizes) return undefined;

      if (sieveSizes.some((s: ISieveSize) => !s.Size || s.Size.uom !== sieveSizes[0].Size!.uom))
        throw new Error('Cannot compare sieves sizes. Sizes are missing or are defined with inconsistent units.');

      sieveSizes = sortBy(sieveSizes, (s) => Number(s.Size!.value));
      let guardSieveSizeIndex = sieveSizes.findIndex((s: ISieveSize) => s.Id === selectedOversizeSieveApertureSize?.Id);
      if (guardSieveSizeIndex === -1)
        throw new Error(
          `A sieve size with id ${selectedOversizeSieveApertureSize?.Id} was not found. This sieve size should be available for a worksheet of this type.`
        );

      return sieveSizes[guardSieveSizeIndex + 1];
    }),
    deepEquals
  );

  return (
    <SectionGroup heading="Material Mass">
      <ListFieldSection>
        <WorksheetBalanceFieldSet name="Balance" label="Balance" />
        <Field input={<WorksheetEquipmentDropdown />} equipmentType="oven" name="Oven" />
        <Field input={<TextInput />} name="TareID" maxLength={20} />
        <Field input={<NumericInput />} name="TareMass" />
        <Field input={<NumericInput />} name="TareAndTotalSampleWetMass" />
        <Field input={<TextOutput />} name="TotalSampleWetMass" />
        <Field input={<TextOutput />} name="TotalSampleDryMass" />

        {oversizeMaterialRemovedBy === OversizeMaterialRemovedByType.WetSieving && (
          <>
            <FieldSet>
              <Field
                input={<WorksheetEquipmentDropdown />}
                equipmentType="sieve"
                name="WashingSieve"
                queryParams={{
                  SieveSize: `${selectedOversizeSieveApertureSize?.Size?.value} ${selectedOversizeSieveApertureSize?.Size?.uom}`,
                }}
              />

              <ButtonGroup>
                <Button onClick={() => updateMoulds([...guardSieves, {}])}>Add Guard Sieve</Button>
                {guardSieves.length > 0 && (
                  <ConfirmDialog
                    confirm={() => !isNil(guardSieves[guardSieves.length - 1].Sieve)}
                    confirmText="This action will delete the last selected guard sieve and cannot be undone. Continue?"
                    confirmButtonText="Remove Guard Sieve"
                    confirmTitle="Remove Guard Sieve"
                  >
                    <Button error onClick={() => updateMoulds(guardSieves.slice(0, -1))}>
                      Remove Guard Sieve
                    </Button>
                  </ConfirmDialog>
                )}
              </ButtonGroup>
            </FieldSet>

            {guardSieves?.map((_, index) => (
              <Field
                key={`guardSieve${index}`}
                input={<WorksheetEquipmentDropdown />}
                equipmentType="sieve"
                label={`Guard Sieve${guardSieves.length > 1 ? ` ${index + 1}` : ''}`}
                name={`GuardSieves[${index}].Sieve`}
                queryParams={{
                  SieveSize: {
                    value: `${guardSieveMinimumSieveSize?.Size?.value} ${guardSieveMinimumSieveSize?.Size?.uom}`,
                    operator: '>=',
                  },
                }}
              />
            ))}
          </>
        )}
        <Field input={<TextInput />} name="RetainedTareID" maxLength={20} />
        <Field input={<NumericInput />} name="TareMassRetained" />
        <Field input={<NumericInput />} name="TareAndDryMassRetained" />
        <Field input={<TextOutput />} name="DryMassRetained" />
      </ListFieldSection>
    </SectionGroup>
  );
};

BsEnIsoPreparationMaterialMassSection = withInjectedProps(BsEnIsoPreparationMaterialMassSection);

export { BsEnIsoPreparationMaterialMassSection };
export default BsEnIsoPreparationMaterialMassSection;
